<template>
  <div :class="s.wrap">
    <div :class="s.header">
      <i class="fa-regular fa-comments me-2" />スレッド一覧
    </div>
    <Loader :flag="flag.loading">
      <div v-if="!threads.length" :class="s.empty">スレッドがありません</div>
      <ul v-else :class="s.list">
        <li
          v-for="thread in sortedThreads"
          :key="thread.id"
          :class="s.item"
          @click="openModal(thread)">
          <div :class="s.item_icon">
            <i class="fa-solid fa-comments" />
          </div>
          <div :class="s.item_body">
            <div :class="s.item_users">{{ threadUserNames(thread) }}</div>
            <div :class="s.item_preview">{{ latestMessage(thread) ? latestMessage(thread).message : '—' }}</div>
          </div>
          <div :class="s.item_time">
            {{ latestMessage(thread) ? formatTimestamp(latestMessage(thread).created_at, 'MM/DD HH:mm') : '' }}
          </div>
        </li>
      </ul>
    </Loader>

    <!-- 新チャット（talkroom: 実務office運行の配車チャット） -->
    <template v-if="talkrooms.length">
      <div :class="s.header" class="mt-4">
        <i class="fa-regular fa-comments me-2" />新チャット
      </div>
      <Loader :flag="flag.loadingTalkrooms">
        <ul :class="s.list">
          <li
            v-for="talkroom in sortedTalkrooms"
            :key="talkroom.id"
            :class="s.item"
            @click="openTalkroomModal(talkroom)">
            <div :class="s.item_icon">
              <i class="fa-solid fa-comments" />
            </div>
            <div :class="s.item_body">
              <div :class="s.item_users">{{ talkroomUserNames(talkroom) }}</div>
              <div :class="s.item_offices">{{ talkroomOfficeNames(talkroom) }}</div>
              <div :class="s.item_preview">{{ latestTalk(talkroom) ? latestTalk(talkroom).message : '—' }}</div>
            </div>
            <div :class="s.item_time">
              {{ latestTalk(talkroom) ? formatTimestamp(latestTalk(talkroom).created_at, 'MM/DD HH:mm') : '' }}
            </div>
          </li>
        </ul>
      </Loader>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Loader } from '@/components/parts';
import cf from '@/mixins/commonFunctions.js';

export default {
  name: 'threads',
  mixins: [cf],
  components: { Loader },
  data() {
    return {
      flag: { loading: false, loadingTalkrooms: false },
      threads: [],
      talkrooms: [],
    };
  },
  computed: {
    ...mapState(['user']),
    officeId() {
      return this.user.offices?.[0]?.id;
    },
    sortedThreads() {
      return [...this.threads].sort((a, b) => {
        const aMsg = this.latestMessage(a);
        const bMsg = this.latestMessage(b);
        const aTime = aMsg ? aMsg.created_at : a.created_at;
        const bTime = bMsg ? bMsg.created_at : b.created_at;
        return new Date(bTime) - new Date(aTime);
      });
    },
    sortedTalkrooms() {
      return [...this.talkrooms].sort((a, b) => {
        const aTalk = this.latestTalk(a);
        const bTalk = this.latestTalk(b);
        const aTime = aTalk ? aTalk.created_at : a.created_at;
        const bTime = bTalk ? bTalk.created_at : b.created_at;
        return new Date(bTime) - new Date(aTime);
      });
    },
  },
  created() {
    this.getThreads();
    this.getTalkrooms();
  },
  methods: {
    latestMessage(thread) {
      const msgs = thread.threadMessages || [];
      if (!msgs.length) return null;
      return msgs.reduce((latest, m) => (new Date(m.created_at) > new Date(latest.created_at) ? m : latest));
    },
    threadUserNames(thread) {
      const userIds = thread.threadUsers.map((tu) => tu.user_id);
      if (userIds.length === 1 && userIds.includes(this.user.id)) return 'オペレーター';
      const otherUsers = thread.threadUsers.filter((tu) => tu.user_id !== this.user.id);
      return otherUsers.map((u) => u.user?.username).filter((name) => name).join(',');
    },
    async getThreads() {
      if (!this.officeId) return;
      this.flag.loading = true;
      try {
        const res = await this.axios.get('/v1/thread/get/list', {
          params: {
            officeIds: [this.officeId],
            userIds: [this.user.id],
            withUsers: 1,
            withMessages: 1,
          },
        });
        this.threads = res.data.threads?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.flag.loading = false;
      }
    },
    openModal(thread) {
      const userIds = (thread.threadUsers || []).map((tu) => tu.user_id);
      this.$store.dispatch('modal/contents/showModal', {
        modalName: 'ThreadMessages',
        modalTitle: `スレッド - ${this.threadUserNames(thread)}`,
        modalWidth: '95%',
        data: { userIds, officeId: this.officeId },
      });
    },

    latestTalk(talkroom) {
      const talks = talkroom.talks || [];
      if (!talks.length) return null;
      return talks.reduce((latest, t) => (new Date(t.created_at) > new Date(latest.created_at) ? t : latest));
    },
    talkroomUserNames(talkroom) {
      const otherUsers = (talkroom.relations || [])
        .filter((r) => r.foreign_type === 11 && r.foreign_id !== this.user.id);
      if (!otherUsers.length) return 'オペレーター';
      return otherUsers.map((r) => r.user?.username).filter((name) => name).join(',');
    },
    talkroomOfficeNames(talkroom) {
      return (talkroom.relations || [])
        .filter((r) => r.foreign_type === 21)
        .map((r) => r.office?.name || '不明')
        .join(' / ');
    },
    async getTalkrooms() {
      this.flag.loadingTalkrooms = true;
      try {
        const res = await this.axios.get('/v1/talkroom/get/list', {
          params: {
            userIds: [this.user.id],
            withRelations: 1,
            withTalks: 1,
          },
        });
        this.talkrooms = res.data.talkrooms?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.flag.loadingTalkrooms = false;
      }
    },
    openTalkroomModal(talkroom) {
      this.$store.dispatch('modal/contents/showModal', {
        modalName: 'TalkroomMessages',
        modalTitle: `${this.talkroomUserNames(talkroom)}（${this.talkroomOfficeNames(talkroom)}）`,
        modalWidth: '95%',
        data: { talkroomId: talkroom.id, talkroom },
      });
    },
  },
};
</script>

<style lang="scss" module="s">
.wrap {
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
}

.header {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 20px;
}

.empty {
  padding: 40px;
  text-align: center;
  color: var(--label-secondary, #888);
}

.list {
  list-style: none;
  padding: 0;
  margin: 0;
  border: 1px solid var(--border-gray, #e0e0e0);
  border-radius: 8px;
  overflow: hidden;
}

.item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background-color: #fff;
  cursor: pointer;
  transition: background-color 0.15s;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-gray, #e0e0e0);
  }

  &:hover {
    background-color: var(--bg-gray, #f5f5f5);
  }
}

.item_icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(13, 110, 253, 0.1);
  color: var(--bs-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.item_body {
  flex: 1;
  min-width: 0;
}

.item_users {
  font-weight: bold;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item_preview {
  color: var(--label-secondary, #888);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item_offices {
  color: var(--label-secondary, #888);
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item_time {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--label-secondary, #aaa);
  align-self: flex-start;
  padding-top: 2px;
}
</style>
